import React, { useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthModal = ({ onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  // Pull a pending referral code from the URL or localStorage so signups
  // captured during a referral campaign reward the inviter automatically.
  // Supports both the legacy raw-string format and the new JSON format
  // written by useReferralCapture (`{code, saved_at, expires_at}`).
  const pendingReferral = (() => {
    try {
      const urlParam = new URLSearchParams(window.location.search).get('ref');
      if (urlParam) return urlParam.trim().toLowerCase();
      const cached = localStorage.getItem('audiostride_ref');
      if (!cached) return null;
      // Try JSON first (new format), fall back to raw string.
      try {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object' && parsed.code) {
          if (parsed.expires_at && parsed.expires_at < Date.now()) return null;
          return String(parsed.code).trim().toLowerCase();
        }
      } catch (_) {
        // Legacy plain-string cache — keep supporting it.
      }
      return cached.trim().toLowerCase();
    } catch (_) { return null; }
  })();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      let payload;
      if (isLogin) {
        payload = { email: formData.email, password: formData.password };
      } else {
        payload = { ...formData };
        if (pendingReferral) payload.referral_code = pendingReferral;
      }

      const response = await axios.post(`${API}${endpoint}`, payload);
      login(response.data.token, response.data.user);
      // Consume the referral cookie so it doesn't apply twice
      if (!isLogin && pendingReferral) {
        try { localStorage.removeItem('audiostride_ref'); } catch (_) { /* noop */ }
        // Log the referral funnel event — signals to the analytics dashboard
        // that a referral campaign converted into a new account.
        try {
          axios.post(`${API}/referrals/track-event`, {
            event_type: 'account_created',
            ref_code: pendingReferral,
            metadata: { new_user_id: response.data?.user?.user_id || null },
          }).catch(() => {});
        } catch (_) { /* noop */ }
      }
      toast.success(isLogin ? 'Welcome back!' : (pendingReferral ? 'Account created! +250 welcome Strides 🎉' : 'Account created successfully!'));
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-floating max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900 font-sans">
              {isLogin ? 'Sign In' : 'Create Account'}
            </h2>
            <button
              onClick={onClose}
              data-testid="close-auth-modal"
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && pendingReferral && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2" data-testid="referral-banner">
                <span className="text-lg">🎁</span>
                <p className="text-xs text-slate-700">
                  Joining via <span className="font-black">@{pendingReferral}</span>'s invite — you'll both earn Strides
                </p>
              </div>
            )}
            {!isLogin && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  data-testid="auth-name-input"
                  required={!isLogin}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                  placeholder="Your name"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                data-testid="auth-email-input"
                required
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                data-testid="auth-password-input"
                required
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 text-white py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:cursor-not-allowed font-sans"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className="text-teal-600 hover:text-teal-700 font-semibold transition-colors font-body"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;